// The "pending edits" machinery shared by the two schedule editors (weekly
// working hours and the special-days calendar): both keep a `serverState` map
// of last-confirmed values plus a `pending` map of unsaved edits, and both use
// the same open/close/day-off fields row and Reset/Save action row. What
// Reset/Save actually send differs per editor, so those handlers are wired up
// by the caller.
import { t, createField } from './context';
export function createScheduleState(initial) {
    return { serverState: new Map(initial), pending: new Map(), active: new Set() };
}
export function scheduleEffectiveValue(state, key) {
    return state.pending.get(key) ?? state.serverState.get(key) ?? { openTime: null, closeTime: null, isClosed: false };
}
// Mirrors the server's validateScheduleDayPayload so an invalid day is caught
// before the request instead of coming back as a generic saveError. A closed
// day needs no times; an open day needs both, with open earlier than close
// (zero-padded "HH:MM" strings order correctly under a plain comparison).
export function isScheduleValueValid(value) {
    if (value.isClosed)
        return true;
    if (!value.openTime || !value.closeTime)
        return false;
    return value.openTime < value.closeTime;
}
// Builds the open/close/day-off fields row. Editing a field writes into
// `state.pending` for every key in `state.active` and calls `onApplied` so the
// caller can re-render its own active/dirty highlighting (weekday bar vs
// calendar grid).
export function createScheduleFields(state, closedLabelText, onApplied) {
    const fieldsRow = document.createElement('div');
    fieldsRow.className = 'admin__time-fields';
    const openField = createField(t('admin.workingHours.openLabel'), 'time');
    const closeField = createField(t('admin.workingHours.closeLabel'), 'time');
    openField.input.required = false;
    closeField.input.required = false;
    const closedWrapper = document.createElement('label');
    closedWrapper.className = 'admin__field admin__field--checkbox';
    const closedCheckbox = document.createElement('input');
    closedCheckbox.type = 'checkbox';
    const closedLabel = document.createElement('span');
    closedLabel.textContent = closedLabelText;
    closedWrapper.append(closedCheckbox, closedLabel);
    fieldsRow.append(openField.wrapper, closeField.wrapper, closedWrapper);
    function updateFieldsDisabled() {
        const disabled = state.active.size === 0 || closedCheckbox.checked;
        openField.input.disabled = disabled;
        closeField.input.disabled = disabled;
    }
    function syncFieldsFromActive() {
        const active = [...state.active];
        closedCheckbox.disabled = active.length === 0;
        if (active.length === 0) {
            openField.input.value = '';
            closeField.input.value = '';
            closedCheckbox.checked = false;
            updateFieldsDisabled();
            return;
        }
        const value = scheduleEffectiveValue(state, active[active.length - 1]);
        openField.input.value = value.openTime ?? '';
        closeField.input.value = value.closeTime ?? '';
        closedCheckbox.checked = value.isClosed;
        updateFieldsDisabled();
    }
    function applyFieldsToActive() {
        if (state.active.size === 0)
            return;
        const value = {
            isClosed: closedCheckbox.checked,
            // An empty <input type="time"> reads back as "" — coalesced to null so
            // it matches the "no value set" shape the save API expects.
            openTime: closedCheckbox.checked ? null : openField.input.value || null,
            closeTime: closedCheckbox.checked ? null : closeField.input.value || null,
        };
        for (const key of state.active) {
            state.pending.set(key, value);
        }
        onApplied();
    }
    openField.input.addEventListener('input', applyFieldsToActive);
    closeField.input.addEventListener('input', applyFieldsToActive);
    closedCheckbox.addEventListener('change', () => {
        updateFieldsDisabled();
        applyFieldsToActive();
    });
    return { fieldsRow, syncFieldsFromActive };
}
export function createScheduleActions(pendingSize) {
    const errorEl = document.createElement('p');
    errorEl.className = 'admin__error';
    errorEl.hidden = true;
    const actionsRow = document.createElement('div');
    actionsRow.className = 'admin__schedule-actions';
    const resetButton = document.createElement('button');
    resetButton.type = 'button';
    resetButton.className = 'admin__reset';
    resetButton.textContent = t('admin.workingHours.reset');
    resetButton.disabled = true;
    const saveButton = document.createElement('button');
    saveButton.type = 'button';
    saveButton.className = 'cta admin__submit';
    saveButton.textContent = t('admin.workingHours.save');
    actionsRow.append(resetButton, saveButton);
    function updateActionsState() {
        resetButton.disabled = pendingSize() === 0;
    }
    return { errorEl, actionsRow, resetButton, saveButton, updateActionsState };
}
